/**
 * #1150 — the Novig price on a main-board team row.
 *
 * One pill per team row: that team's own moneyline outcome, in Novig's native
 * probability (never cents — a native contract pays $0.01, so a cents label
 * would read as a $1 contract). Maker shows the best bid, taker the best ask;
 * there is no last-trade price, so market mode shows none. A fixed-height
 * status line carries stale age / withdrawn / missing quote so the row never
 * reflows when readiness changes.
 */
import { cn } from '@/lib/infra/utils';
import { Td } from '@/components/atoms/TableCells';
import {
  novigPillProbability,
  presentNovigFeed,
  type NovigBoardQuote,
} from '@/lib/novig/presenter';
import type { PriceMode, TeamSide } from '../types';

export interface NovigBoardClick {
  outcomeId: string;
  probability: string | null;
}

export interface NovigBoardCellProps {
  quote: NovigBoardQuote | null;
  now: number;
  teamSide: TeamSide;
  priceMode: PriceMode;
  isFinal: boolean;
  onClick(side: TeamSide, click: NovigBoardClick): void;
}

export function NovigBoardCell({ quote, now, teamSide, priceMode, isFinal, onClick }: NovigBoardCellProps) {
  const outcome = quote?.[teamSide] ?? null;
  if (isFinal || !quote || !outcome) {
    return (
      <Td className="whitespace-nowrap text-center">
        <span className="text-muted-foreground/40" title="No Novig market for this team">—</span>
      </Td>
    );
  }
  const feed = presentNovigFeed(quote.feed, now);
  const probability = novigPillProbability(quote, teamSide, priceMode);
  const status = feed.status || (priceMode === 'market' ? 'No last trade' : ' ');
  return (
    <Td className="whitespace-nowrap text-center">
      <div className="flex w-20 justify-center">
        <button
          type="button"
          disabled={feed.blocksPlacement}
          data-testid={`pill-novig-${teamSide}`}
          title={feed.reason ? `Not tradable now: ${feed.reason}` : `${outcome.label} — click to open the Novig ticket`}
          onClick={(e) => {
            e.stopPropagation();
            onClick(teamSide, { outcomeId: outcome.outcomeId, probability });
          }}
          className={cn(
            'bg-muted min-w-[6ch] rounded px-1.5 py-0.5 font-mono text-sm tabular-nums',
            'enabled:hover:bg-emerald-500/20 disabled:cursor-not-allowed disabled:opacity-50',
          )}
        >
          {probability ?? '—'}
        </button>
      </div>
      <div className="h-3 text-[10px] leading-3 text-muted-foreground" data-testid={`novig-quote-status-${teamSide}`}>
        {status}
      </div>
    </Td>
  );
}
